import React from 'react';
import { SimpleContactForm } from '../components/ContactForm';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { companyInfo } from '../data';
import { motion } from 'framer-motion';

export default function ContactPage() {
  return (
    <div className="pt-24 lg:pt-32 bg-slate-50 min-h-screen">
      {/* Hero Header */}
      <div className="bg-slate-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#33c3f7_1px,transparent_1px)] [background-size:20px_20px]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight mb-6"
          >
            Book din tid hos <span className="text-sky-400">KC Detailer</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto"
          >
            Udfyld formularen herunder for at bestille tid til din bilpleje. Vi bekræfter din booking hurtigst muligt.
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 pb-24 relative z-20">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          
          {/* Contact Info Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-slate-200/60 border border-slate-100"
            >
              <h3 className="text-xl font-black mb-8 uppercase tracking-wider text-slate-900">Kontaktoplysninger</h3>
              
              <div className="space-y-8">
                <div className="flex items-start gap-5">
                  <div className="bg-sky-50 p-3.5 rounded-2xl text-sky-500 shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Ring til os</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-lg font-bold text-slate-900 hover:text-sky-500 transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="bg-sky-50 p-3.5 rounded-2xl text-sky-500 shrink-0">
                    <Mail size={24} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">E-mail</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-lg font-bold text-slate-900 hover:text-sky-500 transition-colors lowercase">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="bg-sky-50 p-3.5 rounded-2xl text-sky-500 shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Adresse</div>
                    <p className="text-lg font-bold text-slate-900">
                      {companyInfo.address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="bg-sky-50 p-3.5 rounded-2xl text-sky-500 shrink-0">
                    <Clock size={24} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-1">Åbningstider</div>
                    <p className="text-lg font-bold text-slate-900">
                      {companyInfo.openingHours}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Socials / Extra Card */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-blue-900 rounded-[2.5rem] p-8 text-white shadow-xl shadow-blue-900/20"
            >
              <h4 className="font-bold text-xl mb-4">Følg vores arbejde</h4>
              <p className="text-blue-100/70 mb-6">Se de seneste resultater og få tips til vedligeholdelse af din bil på vores sociale medier.</p>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <span className="font-bold">IG</span>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <span className="font-bold">FB</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Form Area */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <SimpleContactForm />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
